'use client';

import Image from 'next/image';
import { useRef, Fragment } from 'react';
import { easeIn, easeInOut, easeOut, motion, useScroll, useTransform } from 'framer-motion';
import { PortableTextBlock } from 'sanity';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { useIsMobile } from '@/hooks/useIsMobile';
import ChipText from '@/components/global/ChipText';
import {GROQImage} from "@/sanity/lib/types";
import qualitySectionBg from '@/public/prices/qualitySectionBg.png';
import qualitySectionBgMobile from '@/public/home/qualitySectionBgMobile.png';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/shadcn/ui/accordion';

interface IQualityProps {
  title: PortableTextBlock[];
  items: {
    icon: GROQImage;
    title: string;
    answer: string;
  }[];
  description: string;
}

export default function Quality({
  title,
  items,
  description,
}: IQualityProps) {
  const isMobile = useIsMobile();

  return (
    <section className="flex flex-col w-full items-end justify-center lg:h-full lg:min-h-[124.875rem] pt-[60.66rem] lg:py-0 lg:pr-[16.12rem] relative">
      <div className="absolute top-0 left-0 w-full lg:w-1/2 h-[70.5rem] lg:h-full z-0">
        <Image
          src={isMobile ? qualitySectionBgMobile : qualitySectionBg}
          alt="Image Background"
          layout="fill"
          className="!w-full !h-full object-cover"
          quality={100}
        />
      </div>

      <div className="flex flex-col gap-[5rem] lg:gap-[9rem] w-full lg:max-w-[95.875rem] relative z-10 px-[2rem] lg:px-0">
        <div className="flex flex-col gap-[3rem] lg:gap-[2rem] prose-headings:mb-0 prose-p:mb-0 lg:prose-p:text-[2.5rem] prose-p:opacity-[64%]">
          <PortableText value={title} components={richTextComponent} />
        </div>

        <Accordion type="single" collapsible className="flex flex-col">
          {items?.length > 0 && items?.map((item, index) => (
            <AccordionItem key={index} value={`item-${index}`} className="flex flex-col border-b-[.125rem] border-[#D5D2B2] py-[3rem]">
              <AccordionTrigger className="w-fit hover:no-underline" useToggleIcon>
                <div className="flex items-center gap-[3rem] lg:gap-[4rem]">
                  <div className="flex flex-col w-[7rem] lg:w-[8rem] h-[7rem] lg:h-[8rem] items-center justify-center bg-green flex-shrink-0">
                    <Image
                      className="!w-[3rem] lg:!w-[4rem] aspect-square"
                      src={item.icon.src}
                      width={item.icon.width}
                      height={item.icon.height}
                      quality={100}
                      alt=""
                    />
                  </div>
                  <h3 className={`text-[2.5rem] lg:text-[4rem] font-[400] leading-[140%] lg:leading-[120%] font-inter-tight mb-0 text-left ${isMobile && (index === 0 || index === 1) ? 'max-w-[22rem]' : 'max-w-[27rem]'} lg:max-w-none`}>{item.title}</h3>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pl-[10rem] lg:pl-[12rem] pt-[2rem] lg:pt-0">
                <p className="text-[2rem] lg:text-[2.5rem] font-[400] text-[#072922]/50 leading-[140%] font-inter-tight max-w-[60.625rem] mb-0">{item.answer}</p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <p className="text-fakeblack/[64%] text-[2.5rem] font-[400] leading-[140%] mb-0">
          {description}
        </p>
      </div>
    </section>
  );
}
